if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.additionalPropertyGetters = [];
      }

      registerAdditionalPropertyGetters(callback) {
        this.additionalPropertyGetters.push(callback);
      }

      unregisterAdditionalPropertyGetters(callback) {
        this.additionalPropertyGetters = this.additionalPropertyGetters.filter((c) => c !== callback);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        //check for gift card recipient form and validate it

        const recipientForm = this.querySelector('recipient-form');
        if (recipientForm && typeof recipientForm.validate === 'function') {
          const isValid = recipientForm.validate();
          if (!isValid) {
            this.submitButton?.classList.remove('loading');
            this.submitButton?.removeAttribute('aria-disabled');
            return;
          }
        }

        this.submit(evt, { runPropertyGetters: true });
      }

      async submit(evt, { runPropertyGetters = false, additionalProperties = {} } = {}) {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (runPropertyGetters) {
          try {
            additionalProperties = {
              ...additionalProperties,
              ...(await Promise.all(this.additionalPropertyGetters.map((callback) => callback()))).reduce(
                (acc, properties) => ({ ...acc, ...properties }),
                {},
              ),
            };
          } catch (err) {
            this.handleErrorMessage(err.message);
            this.removeLoadingState();
            return;
          }
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          Object.entries(additionalProperties).forEach(([key, val]) => {
            formData.append(`properties[${key}]`, val);
          });
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      const onCartPage = window.location.pathname === '/cart';
                      this.cart.renderContents(response, { shouldOpen: !onCartPage });
                      if (onCartPage) {
                        publish(PUB_SUB_EVENTS.toastOpen, {
                          message: 'Your item has been added to cart!',
                          status: 'success',
                        });
                      }
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
            let colorName;
            if (this.dataset.productColor) {
              colorName = this.dataset.productColor;
            } else {
              colorName = response.options_with_values.find((opt) => opt.name.toLowerCase() === 'color')?.value;
            }
            const itemId = `${response.product_id}${colorName ? `_${colorName}` : ''}`;
            // example location inside the add-to-cart / submit flow where `response.price` is used
            // previously:
            // const price = response.price;
            const formattedPrice = window.formatInsightsPrice
              ? window.formatInsightsPrice(response.price)
              : (response.price / 100).toFixed(2);
              
            // send both keys (string) so Stylitics receives the formatted value regardless of which key it honours
            window.insights.trackAddItemToCart({
              currency: Shopify.currency.active || 'USD',
              itemId: itemId,
              placement: 'main',
              price: formattedPrice,
              quantity: parseInt(formData.get('quantity')) || 1,
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.removeLoadingState();
            if (evt) {
              CartPerformance.measureFromEvent('add:user-action', evt);
            }
          });
      }

      removeLoadingState() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
